import { useState } from 'react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { X, Upload, Loader2, Calendar } from 'lucide-react'
import { uploadFreshTattooPhoto } from '@/lib/tattooPhotoUpload'
import { addTattooToRegistry } from '@/lib/clientRegistryData'
import { DateSelectionDialog } from '@/components/shared/DateSelectionDialog'
import { format } from 'date-fns'
import type { Booking } from '@/types/booking'

interface AddTattooDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  artistId: string
  clientId: string
  onSuccess?: () => void
  initialImageUrl?: string // For hybrid approach - image from chat
  booking?: Booking // Link to a booking
}

export function AddTattooDialog({
  open,
  onOpenChange,
  artistId,
  clientId,
  onSuccess,
  initialImageUrl,
  booking
}: AddTattooDialogProps) {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [previewUrls, setPreviewUrls] = useState<string[]>(initialImageUrl ? [initialImageUrl] : [])
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showDateDialog, setShowDateDialog] = useState(false)

  // Form fields
  const [description, setDescription] = useState(booking?.shortSummary || '')
  const [tattooDate, setTattooDate] = useState<Date>(
    booking?.scheduledTimestamp 
      ? new Date(booking.scheduledTimestamp)
      : new Date()
  )
  const [caption, setCaption] = useState('')

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return

    // Limit to 3 photos
    const newFiles = files.slice(0, 3 - selectedFiles.length)
    
    // Create preview URLs
    const newPreviewUrls = newFiles.map(file => URL.createObjectURL(file))
    
    setSelectedFiles(prev => [...prev, ...newFiles])
    setPreviewUrls(prev => [...prev, ...newPreviewUrls])
  }

  const removeImage = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index))
    setPreviewUrls(prev => {
      const newUrls = prev.filter((_, i) => i !== index)
      // Clean up object URL
      if (prev[index] && !initialImageUrl) {
        URL.revokeObjectURL(prev[index])
      }
      return newUrls
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (selectedFiles.length === 0 && !initialImageUrl) {
      setError('Please select at least one photo')
      return
    }

    setUploading(true)
    setError(null)

    try {
      // Create the tattoo entry
      const tattooId = await addTattooToRegistry(artistId, clientId, {
        description: description.trim() || undefined,
        bookingId: booking?.id, // Link to booking
        createdAt: tattooDate
      })

      // Upload photos (or use the initial URL from chat)
      if (initialImageUrl) {
        // For hybrid approach: we already have a URL from chat
        // We'll need to handle this differently - potentially copy the image or just reference it
        console.log('Using image from chat:', initialImageUrl)
        // TODO: Implement copying image from chat storage to tattoo storage
      } else {
        // Upload each file
        for (const file of selectedFiles) {
          await uploadFreshTattooPhoto(
            artistId,
            clientId,
            tattooId,
            file,
            caption.trim() || undefined
          )
        }
      }

      // Success!
      onSuccess?.()
      resetForm()
      onOpenChange(false)
    } catch (err) {
      console.error('Error adding tattoo:', err)
      setError(err instanceof Error ? err.message : 'Failed to add tattoo')
    } finally {
      setUploading(false)
    }
  }

  const resetForm = () => {
    // Reset form
    setSelectedFiles([])
    setPreviewUrls(initialImageUrl ? [initialImageUrl] : [])
    setDescription('')
    setTattooDate(booking?.scheduledTimestamp ? new Date(booking.scheduledTimestamp) : new Date())
    setCaption('')
    setError(null)
  }

  const handleClose = () => {
    // Clean up preview URLs
    previewUrls.forEach(url => {
      if (!initialImageUrl) {
        try {
          URL.revokeObjectURL(url)
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    })
    
    resetForm()
    onOpenChange(false)
  }

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent 
        side="bottom" 
        className="h-[90vh] flex flex-col p-6 max-w-[calc(700px+1.5rem)] mx-auto left-0 right-0" 
        hideCloseButton
      >
        <SheetHeader className="pb-4">
          <SheetTitle className="text-xl font-semibold">
            Add Tattoo to Timeline
          </SheetTitle>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto flex flex-col">
          <div className="space-y-5">
            {/* Photo upload */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Photos (up to 3)</Label>
              
              {/* Preview grid */}
              {previewUrls.length > 0 && (
                <div className="grid grid-cols-3 gap-3 mb-3">
                  {previewUrls.map((url, index) => (
                    <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-muted border border-border">
                      <img
                        src={url}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      {!uploading && (
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-2 right-2 w-6 h-6 rounded-full bg-background border border-border flex items-center justify-center hover:bg-muted transition-colors"
                        >
                          <X className="w-4 h-4 text-foreground" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Upload button */}
              {previewUrls.length < 3 && (
                <label className="flex flex-col items-center justify-center gap-2 px-4 py-6 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-primary hover:bg-muted/30 transition-colors">
                  <Upload className="w-5 h-5 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground text-center">
                    {previewUrls.length === 0 ? 'Select photos' : 'Add more photos'}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
              )}
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description" className="text-sm font-medium mb-2 block">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Dragon with cherry blossoms..."
                rows={3}
                disabled={uploading}
              />
            </div>

            {/* Date */}
            <div>
              <Label htmlFor="tattooDate" className="text-sm font-medium mb-2 block">Date</Label>
              <Button
                variant="outline"
                className="w-full justify-start text-left font-normal"
                onClick={() => setShowDateDialog(true)}
                type="button"
              >
                <Calendar className="mr-2 h-4 w-4 text-foreground" />
                {format(tattooDate, 'PPP')}
              </Button>
            </div>

            {/* Caption */}
            <div>
              <Label htmlFor="caption" className="text-sm font-medium mb-2 block">Photo Caption (optional)</Label>
              <Input
                id="caption"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Fresh after the session"
                disabled={uploading}
              />
            </div>

            {/* Error message */}
            {error && (
              <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-lg">
                {error}
              </div>
            )}
          </div>

          {/* Actions - Sticky at bottom */}
          <div className="flex gap-3 pt-6 mt-auto">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={uploading}
              className="flex-1 h-12"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={uploading || (selectedFiles.length === 0 && !initialImageUrl)}
              className="flex-1 h-12"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                'Add Tattoo'
              )}
            </Button>
          </div>
        </form>

        {/* Date Selection Dialog */}
        <DateSelectionDialog
          isOpen={showDateDialog}
          onOpenChange={setShowDateDialog}
          selectedDates={[tattooDate]}
          onDateSelect={(date) => {
            setTattooDate(date)
            setShowDateDialog(false)
          }}
          onDateRangeSelect={(startDate) => {
            // For single date selection, just use the start date
            setTattooDate(startDate)
            setShowDateDialog(false)
          }}
          title="Select Tattoo Date"
          singleDateOnly={true}
        />
      </SheetContent>
    </Sheet>
  )
}


